import { useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { UiTheme } from '@ticarishift/api-client/platform';
import { isApiProblem, networkProblem, type ApiProblem, type RestoreResult } from '@ticarishift/api-client/runtime';
import { t } from '@ticarishift/i18n';
import { api, apiClient, setSessionHandlers } from '../api/client.ts';
import { applyThemePreference } from '../theme/theme.ts';
import { resolveIdentity, type PlatformIdentity } from './identity.ts';
import { PlatformPermissions, type PlatformPermission } from './permissions.ts';
import { openSessionChannel, type SessionChannel, type SessionMessage } from './session-channel.ts';

/**
 * Oturum açma ekranındaki tek satırlık açıklama: süre doldu (8 saatlik mutlak ömür ya da yenileme reddi), platform
 * erişimi kapatıldı ya da kişi oturumu bu sekmede kendisi kapattı.
 */
export type AnonymousNotice = 'expired' | 'disabled' | 'signedOut';

/**
 * Platform oturumunun durumu: açılışta çerezle geri yüklenir ve kimlik (`GET /api/platform/me`) okunur. `unreachable` sunucuya ulaşılamadı (oturum
 * korunur, yeniden denenir); `anonymous` oturum yok (oturum açma ekranı).
 */
export type SessionStatus =
  | { readonly kind: 'loading' }
  | { readonly kind: 'anonymous'; readonly notice?: AnonymousNotice }
  | { readonly kind: 'unreachable'; readonly problem: ApiProblem }
  | { readonly kind: 'ready'; readonly identity: PlatformIdentity };

type SessionActions = {
  /**
   * Oturum açma yanıtındaki jetonla (yalnız TOTP ya da kurtarma kodundan sonra gelir) oturumu başlatır ve kimliği
   * (`GET /api/platform/me`) okur. Sunucuya ulaşılamazsa `null` döner ve durum "Bağlantı kurulamadı" olur.
   */
  signIn: (tokens: { readonly accessToken: string }) => Promise<PlatformIdentity | null>;
  signOut: () => Promise<void>;
  /** Açılıştaki geri yüklemeyi yeniden dener ("Bağlantı kurulamadı" ekranı). */
  retry: () => Promise<void>;
  /** Rol değişmiş olabilir (ör. 403 `permission_denied`): kimlik yeniden okunur, menü güncellenir. */
  reloadIdentity: () => Promise<void>;
  /**
   * Tema tercihini kaydeder (`PATCH /api/platform/me/ui-preferences`; S23). Tema hemen uygulanır; kayıt başarısız olursa
   * önceki tercihe dönülür ve hata çağırana gider.
   */
  setThemePreference: (theme: UiTheme) => Promise<void>;
};

type SessionState = SessionActions & { readonly status: SessionStatus };

const SessionContext = createContext<SessionState | null>(null);

function noticeFor(problem: ApiProblem | undefined): AnonymousNotice {
  return problem?.code === 'membership_inactive' ? 'disabled' : 'expired';
}

function asProblem(error: unknown): ApiProblem {
  return isApiProblem(error) ? error : networkProblem(error);
}

/** Çerezle geri yükleme; beklenmeyen hata geçici arıza sayılır. */
async function restoreSession(): Promise<RestoreResult> {
  try {
    return await apiClient.restore();
  } catch (error) {
    return { status: 'transient', problem: asProblem(error) };
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<SessionStatus>({ kind: 'loading' });
  const channel = useRef<SessionChannel | null>(null);
  const identity = useRef<PlatformIdentity | null>(null);
  const reloading = useRef<Promise<void> | null>(null);

  const becomeReady = useCallback((next: PlatformIdentity) => {
    identity.current = next;
    // Sunucudaki tema tercihi esastır (panelle aynı kayıt); depo yalnız bir sonraki ilk boyama içindir.
    applyThemePreference(next.theme);
    setStatus({ kind: 'ready', identity: next });
  }, []);

  const becomeAnonymous = useCallback(
    (notice?: AnonymousNotice) => {
      // Önceki oturumun verisi önbellekten atılır; açılışta oturum yoksa atılacak veri de yoktur.
      const hadSession = identity.current !== null || apiClient.hasSession();
      identity.current = null;
      apiClient.clearSession();
      if (hadSession) {
        queryClient.clear();
      }

      setStatus({ kind: 'anonymous', notice });
    },
    [queryClient],
  );

  /** Kimliği okur; ağ ya da sunucu arızası oturumu kapatmaz ("Bağlantı kurulamadı"). */
  const load = useCallback(async (): Promise<PlatformIdentity | null> => {
    try {
      const next = await resolveIdentity();
      becomeReady(next);
      return next;
    } catch (error) {
      const problem = asProblem(error);
      if (problem.isTransient) {
        setStatus({ kind: 'unreachable', problem });
      } else {
        becomeAnonymous(noticeFor(problem));
      }

      return null;
    }
  }, [becomeAnonymous, becomeReady]);

  const settle = useCallback(
    async (result: RestoreResult) => {
      if (result.status === 'transient') {
        setStatus({ kind: 'unreachable', problem: result.problem ?? networkProblem(undefined) });
        return;
      }

      if (result.status === 'invalid') {
        // Açılışta çerez geçersiz: süre açıklaması gerekmez (kişi yeni geliyor); kapatılmış erişim açıklanır.
        becomeAnonymous(result.problem?.code === 'membership_inactive' ? 'disabled' : undefined);
        return;
      }

      await load();
    },
    [becomeAnonymous, load],
  );

  const restore = useCallback(async () => settle(await restoreSession()), [settle]);

  const onMessage = useCallback(
    (message: SessionMessage) => {
      if (message.type === 'logout') {
        becomeAnonymous();
        return;
      }

      if (identity.current && identity.current.userId === message.userId) {
        return;
      }

      // Başka kişi oturum açtı: önceki oturumun önbelleği atılır, yeni çerezle yüklenir.
      apiClient.clearSession();
      queryClient.clear();
      void restore();
    },
    [becomeAnonymous, queryClient, restore],
  );

  useEffect(() => {
    channel.current = openSessionChannel(onMessage);
    return () => channel.current?.close();
  }, [onMessage]);

  useEffect(() => {
    // Açılışta bir kez; StrictMode'daki ikinci kurulum aynı tek uçuşlu yenilemeyi bekler, ilkinin sonucu atılır.
    let active = true;
    void restoreSession().then((result) => (active ? settle(result) : undefined));
    return () => {
      active = false;
    };
  }, [settle]);

  useEffect(
    () =>
      setSessionHandlers({
        onSessionRejected: (problem) => {
          becomeAnonymous(noticeFor(problem));
          channel.current?.post({ type: 'logout' });
        },
      }),
    [becomeAnonymous],
  );

  const reloadIdentity = useCallback(async () => {
    if (!identity.current) {
      return;
    }

    reloading.current ??= resolveIdentity()
      .then((next) => {
        if (identity.current) {
          becomeReady(next);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        reloading.current = null;
      });
    return reloading.current;
  }, [becomeReady]);

  // Rol oturum açıkken değişebilir (platform sahibi değiştirir; sunucu bir sonraki istekte yeni rolle çalışır): yetki
  // reddi görülünce kimlik yeniden okunur, menü ve eylemler yeniden süzülür.
  useEffect(() => {
    const onError = (error: unknown) => {
      if (isApiProblem(error) && error.code === 'permission_denied') {
        void reloadIdentity();
      }
    };
    const queries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        onError(event.action.error);
      }
    });
    const mutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        onError(event.action.error);
      }
    });
    return () => {
      queries();
      mutations();
    };
  }, [queryClient, reloadIdentity]);

  const actions = useMemo<SessionActions>(
    () => ({
      async signIn(tokens) {
        apiClient.setSession({ accessToken: tokens.accessToken });
        queryClient.clear();
        const next = await load();
        if (next) {
          channel.current?.post({ type: 'login', userId: next.userId });
        }

        return next;
      },
      async signOut() {
        await apiClient.logout();
        becomeAnonymous('signedOut');
        channel.current?.post({ type: 'logout' });
      },
      retry: restore,
      reloadIdentity,
      async setThemePreference(theme) {
        const current = identity.current;
        if (!current) {
          return;
        }

        applyThemePreference(theme);
        try {
          const preferences = await api.patch('/api/platform/me/ui-preferences', { body: { theme } });
          if (identity.current) {
            becomeReady({ ...identity.current, theme: preferences.theme });
          }
        } catch (error) {
          applyThemePreference(identity.current?.theme ?? current.theme);
          throw error;
        }
      },
    }),
    [becomeAnonymous, becomeReady, load, queryClient, reloadIdentity, restore],
  );

  const value = useMemo<SessionState>(() => ({ status, ...actions }), [status, actions]);
  return <SessionContext value={value}>{children}</SessionContext>;
}

/** Oturum durumu ve eylemleri (oturum açma ekranı, kabuk korumaları). */
export function useSessionState(): SessionState {
  const value = use(SessionContext);
  if (!value) {
    throw new Error(t('common.system.sessionProviderMissing'));
  }

  return value;
}

export type ReadySession = SessionActions & {
  readonly identity: PlatformIdentity;
  can: (permission: PlatformPermission) => boolean;
};

/** Açık oturum (yalnız korunan rotalarda; oturum yoksa programlama hatasıdır). */
export function useSession(): ReadySession {
  const state = useSessionState();
  if (state.status.kind !== 'ready') {
    throw new Error(t('common.system.sessionNotReady'));
  }

  const { identity: current } = state.status;
  return { ...state, identity: current, can: (permission) => current.permissions.has(permission) };
}

/** Yetkiye göre gösterim (08 §6 #58): menü ve eylemler oturumun yetkilerine (`GET /api/platform/me`) göre süzülür; sunucu ayrıca denetler. */
export function PermissionGate({ perm, fallback = null, children }: { perm: PlatformPermission; fallback?: ReactNode; children: ReactNode }) {
  return useSession().can(perm) ? children : fallback;
}

/**
 * Ana sayfa: sistem özetini görebilen Genel Bakış'a, göremeyen faturalama rolü Faturalama'ya (S43), ikisini de göremeyen İşletmeler'e
 * gider.
 */
export function homeFor(current: PlatformIdentity): string {
  if (current.permissions.has(PlatformPermissions.SystemRead)) {
    return '/';
  }

  return current.permissions.has(PlatformPermissions.BillingRead) ? '/billing' : '/tenants';
}
